/**
 * @fileoverview Scratch extension for facial expression and emotion detection using Affectiva SDK.
 * This extension allows sprites to interact with detected facial expressions, emotions, and facial landmarks through webcam input.
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const formatMessage = require("format-message");
const Cast = require("../../util/cast");

/**
 * Icon svg to be displayed at left edge of each extension block, encoded as a data URI.
 * @type {string}
 */
const blockIconURI =
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cGF0aCBkPSJNNTAgMTBDMjcuOTEgMTAgMTAgMjcuOTEgMTAgNTBzMTcuOTEgNDAgNDAgNDAgNDAtMTcuOTEgNDAtNDBTNzIuMDkgMTAgNTAgMTB6bTAgNzBjLTE2LjU0IDAtMzAtMTMuNDYtMzAtMzBzMTMuNDYtMzAgMzAtMzAgMzAgMTMuNDYgMzAgMzAtMTMuNDYgMzAtMzAgMzB6IiBmaWxsPSIjZmZmIi8+PHBhdGggZD0iTTM1IDQwYzAgMi43NiAyLjI0IDUgNSA1czUtMi4yNCA1LTUtMi4yNC01LTUtNS01IDIuMjQgNSA1em0yMCAwYzAgMi43NiAyLjI0IDUgNSA1czUtMi4yNCA1LTUtMi4yNC01LTUtNS01IDIuMjQgNSA1em0tMTAgMTVjLTUuNTIgMC0xMCA0LjQ4LTEwIDEwaDIwYzAtNS41Mi00LjQ4LTEwLTEwLTEweiIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==";

/**
 * Configuration constants for the face detection system.
 * @readonly
 */
const CONFIG = {
    // Video frame dimensions for face detection
    VIDEO_WIDTH: 480,
    VIDEO_HEIGHT: 360,

    // Detection loop timing (in milliseconds)
    DETECTION_INTERVAL: 100,

    // Default video settings
    DEFAULT_VIDEO_TRANSPARENCY: 50,

    // Expression detection threshold (0-1)
    EXPRESSION_THRESHOLD: 0.5,

    // Affectiva SDK configuration
    AFFECTIVA_CDN_URL: "https://download.affectiva.com/js/3.2.1/affdex.js",
};

/**
 * Enum for video state values.
 * @readonly
 * @enum {string}
 */
const VideoState = {
    /** Video is turned off */
    OFF: "off",
    /** Video is on with normal orientation */
    ON: "on",
    /** Video is on with horizontally flipped display */
    ON_FLIPPED: "on-flipped",
};

/**
 * List of supported facial expressions for detection.
 * Maps display names to Affectiva SDK expression keys.
 * @readonly
 */
const EXPRESSIONS = [
    { text: "smile", value: "smile" },
    { text: "mouth open", value: "mouthOpen" },
    { text: "eye closure", value: "eyeClosure" },
    { text: "eyebrow raise", value: "browRaise" },
    { text: "whistling", value: "lipPucker" },
    { text: "eye widening", value: "eyeWiden" },
    { text: "eyebrow furrow", value: "browFurrow" },
    { text: "nose wrinkle", value: "noseWrinkle" },
    { text: "upper lip raise", value: "upperLipRaise" },
    { text: "lip corner pull", value: "lipCornerDepressor" },
    { text: "chin raise", value: "chinRaise" },
    { text: "smirk", value: "smirk" },
    { text: "attention", value: "attention" },
    { text: "eyelid tighten", value: "lidTighten" },
    { text: "jaw drop", value: "jawDrop" },
    { text: "cheek dimple", value: "dimpler" },
    { text: "cheek raise", value: "cheekRaise" },
    { text: "lip stretch", value: "lipStretch" },
];

/**
 * List of primary emotions for hat blocks and boolean checks.
 * Maps display names to Affectiva SDK emotion keys.
 * @readonly
 */
const PRIMARY_EMOTIONS = [
    { text: "joyful", value: "joy" },
    { text: "sad", value: "sadness" },
    { text: "disgusted", value: "disgust" },
    { text: "angry", value: "anger" },
    { text: "fearful", value: "fear" },
];

/**
 * Complete list of emotions available for reporting.
 * Includes all emotions and emotional dimensions from Affectiva SDK.
 * @readonly
 */
const ALL_EMOTIONS = [
    { text: "joy", value: "joy" },
    { text: "sadness", value: "sadness" },
    { text: "disgust", value: "disgust" },
    { text: "contempt", value: "contempt" },
    { text: "anger", value: "anger" },
    { text: "fear", value: "fear" },
    { text: "surprise", value: "surprise" },
    { text: "valence", value: "valence" },
    { text: "engagement", value: "engagement" },
];

/**
 * Facial landmark points for sprite positioning.
 * Maps display names to Affectiva feature point indices.
 * @readonly
 */
const FACIAL_POINTS = [
    { text: "left ear", value: "0" },
    { text: "left chin", value: "1" },
    { text: "chin", value: "2" },
    { text: "right chin", value: "3" },
    { text: "right ear", value: "4" },
    { text: "left outer eyebrow", value: "5" },
    { text: "left eyebrow", value: "6" },
    { text: "left inner eyebrow", value: "7" },
    { text: "right inner eyebrow", value: "8" },
    { text: "right eyebrow", value: "9" },
    { text: "right outer eyebrow", value: "10" },
    { text: "nose bridge", value: "11" },
    { text: "nose tip", value: "12" },
    { text: "left nostril", value: "13" },
    { text: "nose tip", value: "14" },
    { text: "right nostril", value: "15" },
    { text: "left outer eye crease", value: "16" },
    { text: "left inner eye crease", value: "17" },
    { text: "right inner eye crease", value: "18" },
    { text: "right outer eye crease", value: "19" },
    { text: "left mouth crease", value: "20" },
    { text: "left upper lip point", value: "21" },
    { text: "upper lip", value: "22" },
    { text: "right upper lip point", value: "23" },
    { text: "right mouth crease", value: "24" },
    { text: "right lower lip point", value: "25" },
    { text: "lower lip", value: "26" },
    { text: "left lower lip point", value: "27" },
    { text: "upper lip bottom", value: "28" },
    { text: "lower lip top", value: "29" },
    { text: "left upper eyelid", value: "30" },
    { text: "left lower eyelid", value: "31" },
    { text: "right upper eyelid", value: "32" },
    { text: "right lower eyelid", value: "33" },
];

/**
 * Scratch extension class for facial expression and emotion detection using Affectiva SDK.
 * Provides blocks for sprite movement based on facial landmarks, expression detection, and emotion analysis.
 */
class Scratch3PoseFaceBlocks {
    /**
     * Create a new PoseFace extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * Current video display state
         * @type {string}
         * @private
         */
        this.globalVideoState = VideoState.ON;

        /**
         * Current video transparency percentage (0-100)
         * @type {number}
         * @private
         */
        this.globalVideoTransparency = CONFIG.DEFAULT_VIDEO_TRANSPARENCY;

        /**
         * Loaded Affectiva detector instance
         * @type {object|null}
         * @private
         */
        this.affdexDetector = null;

        /**
         * Most recent face detection result from Affectiva
         * @type {object|null}
         * @private
         */
        this.affdexState = null;

        /**
         * Video frame dimensions for face detection
         * @type {number[]}
         * @private
         */
        this.DIMENSIONS = [CONFIG.VIDEO_WIDTH, CONFIG.VIDEO_HEIGHT];

        // Start face detection if video devices are available
        if (this.runtime.ioDevices) {
            this._loop();
        }

        // Apply default extension settings
        this.projectStarted();
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "poseface",
            name: formatMessage({
                id: "poseface.categoryName",
                default: "Face Sensing",
                description:
                    "Label for the facial expression detection extension category",
            }),
            blockIconURI: blockIconURI,
            blocks: [
                {
                    opcode: "affdexGoToPart",
                    text: formatMessage({
                        id: "poseface.goToPart",
                        default: "go to [AFFDEX_POINT]",
                        description:
                            "Move sprite to the detected position of a specific facial landmark",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        AFFDEX_POINT: {
                            type: ArgumentType.STRING,
                            menu: "AFFDEX_POINT",
                            defaultValue: "0",
                        },
                    },
                },
                {
                    opcode: "affdexWhenExpression",
                    text: formatMessage({
                        id: "poseface.whenExpression",
                        default: "when [EXPRESSION] detected",
                        description:
                            "Hat block that triggers when a facial expression is detected",
                    }),
                    blockType: BlockType.HAT,
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: "EXPRESSION",
                            defaultValue: "smile",
                        },
                    },
                },
                {
                    opcode: "affdexExpressionAmount",
                    text: formatMessage({
                        id: "poseface.expressionAmount",
                        default: "amount of [EXPRESSION]",
                        description:
                            "Report the intensity level of a specific facial expression (0-100)",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: "EXPRESSION",
                            defaultValue: "smile",
                        },
                    },
                },
                {
                    opcode: "affdexIsExpression",
                    text: formatMessage({
                        id: "poseface.isExpression",
                        default: "expressing [EXPRESSION]",
                        description:
                            "Boolean check if a specific facial expression is currently detected",
                    }),
                    blockType: BlockType.BOOLEAN,
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: "EXPRESSION",
                            defaultValue: "smile",
                        },
                    },
                },
                {
                    opcode: "affdexWhenEmotion",
                    text: formatMessage({
                        id: "poseface.whenEmotion",
                        default: "when [EMOTION] feeling detected",
                        description:
                            "Hat block that triggers when a specific emotion is the dominant emotion",
                    }),
                    blockType: BlockType.HAT,
                    arguments: {
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: "EMOTION",
                            defaultValue: "joy",
                        },
                    },
                },
                {
                    opcode: "affdexEmotionAmount",
                    text: formatMessage({
                        id: "poseface.emotionAmount",
                        default: "level of [EMOTION_ALL]",
                        description:
                            "Report the intensity level of a specific emotion (0-100)",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        EMOTION_ALL: {
                            type: ArgumentType.STRING,
                            menu: "EMOTION_ALL",
                            defaultValue: "joy",
                        },
                    },
                },
                {
                    opcode: "affdexIsTopEmotion",
                    text: formatMessage({
                        id: "poseface.isTopEmotion",
                        default: "feeling [EMOTION]",
                        description:
                            "Boolean check if a specific emotion is currently the dominant emotion",
                    }),
                    blockType: BlockType.BOOLEAN,
                    arguments: {
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: "EMOTION",
                            defaultValue: "joy",
                        },
                    },
                },
                {
                    opcode: "videoToggle",
                    text: formatMessage({
                        id: "poseface.videoToggle",
                        default: "turn video [VIDEO_STATE]",
                        description:
                            "Control the display of the camera video preview",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: "VIDEO_STATE",
                            defaultValue: "off",
                        },
                    },
                },
                {
                    opcode: "setVideoTransparency",
                    text: formatMessage({
                        id: "poseface.setVideoTransparency",
                        default: "set video transparency to [TRANSPARENCY]",
                        description:
                            "Adjust the opacity of the camera video preview (0-100)",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_VIDEO_TRANSPARENCY,
                        },
                    },
                },
            ],
            menus: {
                AFFDEX_POINT: {
                    acceptReporters: false,
                    items: FACIAL_POINTS,
                },
                EXPRESSION: {
                    acceptReporters: true,
                    items: EXPRESSIONS,
                },
                EMOTION: {
                    acceptReporters: true,
                    items: PRIMARY_EMOTIONS,
                },
                EMOTION_ALL: {
                    acceptReporters: true,
                    items: ALL_EMOTIONS,
                },
                VIDEO_STATE: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "poseface.videoState.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                        {
                            text: formatMessage({
                                id: "poseface.videoState.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "poseface.videoState.onFlipped",
                                default: "on flipped",
                            }),
                            value: VideoState.ON_FLIPPED,
                        },
                    ],
                },
            },
        };
    }

    /**
     * Main face detection loop that processes video frames.
     * Runs continuously while the extension is active.
     * @private
     */
    async _loop() {
        while (true) {
            const frame = this.runtime.ioDevices.video.getFrame({
                format: "image-data",
                dimensions: this.DIMENSIONS,
            });

            if (frame) {
                try {
                    this.affdexState = await this._estimateAffdexOnImage(frame);
                } catch (err) {
                    console.error("Error detecting face:", err);
                    // Reset state on error to prevent stale data
                    this.affdexState = null;
                }
            }

            await new Promise((r) => setTimeout(r, CONFIG.DETECTION_INTERVAL));
        }
    }

    /**
     * Estimate facial expressions and emotions on a given image using Affectiva SDK.
     * @param {ImageData} imageElement - The image data to analyze
     * @returns {Promise<object|null>} Face detection result or null if no face detected
     * @private
     */
    async _estimateAffdexOnImage(imageElement) {
        const affdexDetector = await this._ensureAffdexLoaded(imageElement);
        affdexDetector.process(imageElement, 0);

        return new Promise((resolve) => {
            const resultListener = function (faces) {
                affdexDetector.removeEventListener(
                    "onImageResultsSuccess",
                    resultListener
                );
                if (faces.length < 1) {
                    resolve(null);
                    return;
                }
                resolve(faces[0]);
            };
            affdexDetector.addEventListener(
                "onImageResultsSuccess",
                resultListener
            );
        });
    }

    /**
     * Ensure Affectiva detector is loaded and initialized.
     * @param {ImageData} imageElement - Image element for detector initialization
     * @returns {Promise<object>} The initialized Affectiva detector
     * @private
     */
    async _ensureAffdexLoaded(imageElement) {
        if (this.affdexDetector) return this.affdexDetector;

        const affdex = await this._loadAffdexScript();
        const affdexStarter = new Promise((resolve) => {
            const width = this.DIMENSIONS[0];
            const height = this.DIMENSIONS[1];
            const faceMode = affdex.FaceDetectorMode.LARGE_FACES;
            const detector = new affdex.PhotoDetector(
                imageElement,
                width,
                height,
                faceMode
            );
            detector.detectAllEmotions();
            detector.detectAllExpressions();
            detector.start();
            this.affdexDetector = detector;
            detector.addEventListener("onInitializeSuccess", resolve);
        });
        await affdexStarter;
        return this.affdexDetector;
    }

    /**
     * Dynamically load Affectiva SDK from CDN.
     * This approach allows the extension to work without bundling the large Affectiva library.
     * @returns {Promise<object>} Promise that resolves with the Affectiva SDK object
     * @private
     */
    _loadAffdexScript() {
        return new Promise((resolve, reject) => {
            // Check if Affectiva is already loaded
            if (window.affdex) {
                resolve(window.affdex);
                return;
            }

            const script = document.createElement("script");
            script.src = CONFIG.AFFECTIVA_CDN_URL;
            script.onload = () => {
                if (window.affdex) {
                    resolve(window.affdex);
                } else {
                    reject(new Error("Affdex not loaded"));
                }
            };
            script.onerror = () =>
                reject(new Error("Failed to load Affdex script"));
            document.head.appendChild(script);
        });
    }

    /**
     * Convert Affectiva coordinates to Scratch coordinate system.
     * Affectiva uses (0,0) at top-left, Scratch uses (0,0) at center.
     * @param {object} coords - The coordinates object with x, y properties
     * @param {number} coords.x - X coordinate from Affectiva
     * @param {number} coords.y - Y coordinate from Affectiva
     * @returns {object} Converted coordinates {x, y}
     * @private
     */
    _convertCoordsToScratch({ x, y }) {
        return {
            x: x - this.DIMENSIONS[0] / 2,
            y: this.DIMENSIONS[1] / 2 - y,
        };
    }

    /**
     * Check if a face is currently detected.
     * @returns {boolean} True if face is detected with feature points available
     * @private
     */
    _hasFace() {
        return this.affdexState && this.affdexState.featurePoints;
    }

    /**
     * Determine if a specific emotion is the top/dominant emotion.
     * @param {string} emotion - The emotion to check
     * @param {Array} emotions - Array of emotion objects to compare against
     * @returns {boolean} True if the specified emotion is dominant
     * @private
     */
    _isTopEmotion(emotion, emotions) {
        if (!this._hasFace() || !this.affdexState.emotions) return false;

        let maxEmotionValue = -Number.MAX_VALUE;
        let maxEmotion = null;

        emotions.forEach((emotionItem) => {
            const emotionValue = this.affdexState.emotions[emotionItem.value];
            if (emotionValue > maxEmotionValue) {
                maxEmotionValue = emotionValue;
                maxEmotion = emotionItem;
            }
        });

        return emotion === maxEmotion?.value;
    }

    /**
     * Initialize extension with default settings when project starts.
     * Called automatically by Scratch runtime.
     */
    projectStarted() {
        this.setVideoTransparency({
            TRANSPARENCY: this.globalVideoTransparency,
        });
        this.videoToggle({ VIDEO_STATE: this.globalVideoState });
    }

    /**
     * Move sprite to the detected position of a specific facial landmark.
     * Block implementation for "go to [AFFDEX_POINT]".
     * @param {object} args - Block arguments
     * @param {string} args.AFFDEX_POINT - Index of facial point to move to
     * @param {object} util - Block utility object with target sprite reference
     */
    affdexGoToPart(args, util) {
        if (!this._hasFace()) return;

        const pointIndex = args.AFFDEX_POINT;
        const part = this.affdexState.featurePoints[pointIndex];
        if (!part) return;

        const { x, y } = this._convertCoordsToScratch(part);

        // Keep sprite within stage bounds
        const boundedPosition = util.target.keepInFence(x, y);
        util.target.setXY(boundedPosition[0], boundedPosition[1]);
    }

    /**
     * Hat block that triggers when a facial expression is detected.
     * Block implementation for "when [EXPRESSION] detected".
     * @param {object} args - Block arguments
     * @param {string} args.EXPRESSION - Name of expression to detect
     * @returns {boolean} True if expression is detected above threshold
     */
    affdexWhenExpression(args) {
        const expression = Cast.toString(args.EXPRESSION);
        return (
            this._hasFace() &&
            this.affdexState.expressions &&
            this.affdexState.expressions[expression] >
                CONFIG.EXPRESSION_THRESHOLD
        );
    }

    /**
     * Report the intensity level of a specific facial expression.
     * Block implementation for "amount of [EXPRESSION]".
     * @param {object} args - Block arguments
     * @param {string} args.EXPRESSION - Name of expression to measure
     * @returns {number} Expression intensity (0-100), or 0 if not detected
     */
    affdexExpressionAmount(args) {
        const expression = Cast.toString(args.EXPRESSION);
        if (!this._hasFace() || !this.affdexState.expressions) return 0;

        const rawValue = this.affdexState.expressions[expression] || 0;
        return parseFloat(Number(rawValue * 100).toFixed(2));
    }

    /**
     * Boolean check if a specific facial expression is currently detected.
     * Block implementation for "expressing [EXPRESSION]".
     * @param {object} args - Block arguments
     * @param {string} args.EXPRESSION - Name of expression to check
     * @returns {boolean} True if expression is detected above threshold
     */
    affdexIsExpression(args) {
        const expression = Cast.toString(args.EXPRESSION);
        return (
            this._hasFace() &&
            this.affdexState.expressions &&
            this.affdexState.expressions[expression] >
                CONFIG.EXPRESSION_THRESHOLD
        );
    }

    /**
     * Hat block that triggers when a specific emotion is the dominant emotion.
     * Block implementation for "when [EMOTION] feeling detected".
     * @param {object} args - Block arguments
     * @param {string} args.EMOTION - Name of emotion to detect
     * @returns {boolean} True if emotion is dominant
     */
    affdexWhenEmotion(args) {
        const emotion = Cast.toString(args.EMOTION);
        return this._isTopEmotion(emotion, PRIMARY_EMOTIONS);
    }

    /**
     * Report the intensity level of a specific emotion.
     * Block implementation for "level of [EMOTION_ALL]".
     * @param {object} args - Block arguments
     * @param {string} args.EMOTION_ALL - Name of emotion to measure
     * @returns {number} Emotion intensity (0-100), or 0 if not detected
     */
    affdexEmotionAmount(args) {
        const emotion = Cast.toString(args.EMOTION_ALL);
        if (!this._hasFace() || !this.affdexState.emotions) return 0;

        const rawValue = this.affdexState.emotions[emotion] || 0;
        return parseFloat(Number(rawValue * 100).toFixed(2));
    }

    /**
     * Boolean check if a specific emotion is currently the dominant emotion.
     * Block implementation for "feeling [EMOTION]".
     * @param {object} args - Block arguments
     * @param {string} args.EMOTION - Name of emotion to check
     * @returns {boolean} True if emotion is dominant
     */
    affdexIsTopEmotion(args) {
        const emotion = Cast.toString(args.EMOTION);
        return this._isTopEmotion(emotion, PRIMARY_EMOTIONS);
    }

    /**
     * Control video preview display state.
     * Block implementation for "turn video [VIDEO_STATE]".
     * @param {object} args - Block arguments
     * @param {string} args.VIDEO_STATE - Desired video state (off/on/on-flipped)
     */
    videoToggle(args) {
        const state = Cast.toString(args.VIDEO_STATE);
        this.globalVideoState = state;

        if (state === VideoState.OFF) {
            this.runtime.ioDevices.video.disableVideo();
        } else {
            this.runtime.ioDevices.video.enableVideo();
            this.runtime.ioDevices.video.mirror = state === VideoState.ON;
        }
    }

    /**
     * Set video preview transparency.
     * Block implementation for "set video transparency to [TRANSPARENCY]".
     * @param {object} args - Block arguments
     * @param {number} args.TRANSPARENCY - Transparency percentage (0-100)
     */
    setVideoTransparency(args) {
        const transparency = Cast.toNumber(args.TRANSPARENCY);

        // Clamp transparency value to valid range
        this.globalVideoTransparency = Math.max(0, Math.min(100, transparency));
        this.runtime.ioDevices.video.setPreviewGhost(
            this.globalVideoTransparency
        );
    }
}

module.exports = Scratch3PoseFaceBlocks;
